// Checkout próprio do Reservei (2026-10-20) — o layout da prévia
// (pages/preview/reservei-checkout.tsx) ligado na API pública do CC.
//
// O checkout padrão (CheckoutFlow) continua servindo os outros SaaS; este só
// entra quando a origem é o Reservei (pages/checkout/[slug].tsx).
//
// A regra que molda a tela: o servidor guarda UMA tentativa de compra por passe
// do Reservei, com plano, ciclo, forma de pagamento, CPF/CNPJ, e-mail e cupom.
// Depois do 1º envio esses campos travam — mudar qualquer um dá 409 no
// servidor. Nome, celular e dados do cartão seguem editáveis: é o que permite
// tentar outro cartão depois de uma recusa sem recomeçar pelo app.

import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Check,
  ChevronDown,
  CircleAlert,
  CreditCard,
  Loader2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { formatBRDateTime, isValidCpfOrCnpj, normalizeDocument } from '@control-center/shared';

import { maskCep, maskCpfCnpjAuto, maskPhone } from '../../lib/masks';
import {
  clearReserveiCheckoutIntent,
  fetchPaymentDetails,
  fetchPaymentStatus,
  fetchPublicPlans,
  submitPublicCheckout,
  validateCoupon,
  type CouponValidationResult,
  type PublicCheckoutPayload,
  type PublicPlanInfo,
} from '../../lib/public-checkout-api';
import { PixIcon } from '../checkout-public/branded-icons';
import { CHECKOUT_TERMS_VERSION } from '../checkout-public/terms';
import {
  resolveSuccessTarget,
  useCheckoutEntry,
  type CheckoutBillingCycle,
} from '../checkout-public/use-checkout-entry';
import {
  classifyCheckoutError,
  type CheckoutErrorInfo,
  type CheckoutErrorKind,
} from './checkout-errors';
import {
  EASE,
  FieldError,
  Footer,
  Header,
  Price,
  RESERVEI_FALLBACK_URL,
  SelectionHighlight,
  displayPlanName,
  formatCardNumber,
  formatCountdown,
  formatExpiry,
  formatPrice,
  isExpiryValid,
  onlyDigits,
  parsePixExpiration,
  PixCodeField,
  safeUrl,
  toAmount,
  useCheckoutPageMeta,
  useIsDesktop,
} from './shell';

export { parsePixExpiration } from './shell';

const SAAS = 'reservei';
export const RESERVEI_PLANS_QUERY_KEY = ['public-plans', SAAS] as const;

/** Mesmo intervalo do CheckoutFlow — o throttle do status (API) é calibrado em cima dele. */
export const PAYMENT_POLL_INTERVAL_MS = 4_000;
const PAYMENT_POLL_MAX_ATTEMPTS = 150; // ~10 min
const CARD_SLOW_AFTER_ATTEMPTS = 15; // ~1 min
const POLL_MAX_CONSECUTIVE_ERRORS = 5;

const PAID_STATUSES = new Set(['paga', 'paga_manual']);
const TERMINAL_UNPAID_STATUSES = new Set(['cancelada', 'failed', 'vencida']);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Estes desfechos podem ter gravado a tentativa no servidor — a trava vale a partir deles.
 * `declined` saiu (27/10): é recusa CONFIRMADA do provedor (nada cobrado), e o
 * servidor passou a aceitar outro cartão, Pix ou ciclo na mesma tentativa. Com a
 * trava, quem teve o cartão recusado só podia "voltar ao Reservei" — e de lá caía
 * no beco da troca de plano (caso Lorena).
 */
const LOCKING_KINDS = new Set<CheckoutErrorKind>(['network', 'generic', 'unknown']);
/** Estes encerram a compra nesta página — o caminho é voltar ao Reservei. */
const BLOCKING_KINDS = new Set<CheckoutErrorKind>(['session', 'plan', 'subscribed', 'restart', 'unknown']);

const BLOCKED_TITLES: Partial<Record<CheckoutErrorKind, string>> = {
  session: 'Sua sessão de compra expirou',
  plan: 'Plano indisponível',
  subscribed: 'Você já tem um plano ativo',
  restart: 'Recomece pelo Reservei',
  unknown: 'Estamos confirmando seu pagamento',
};

// Fallback quando o plano não tem destaques cadastrados no CC (hoje nenhum
// tem). Mesmos textos da prévia — chave é o slug do plano no Reservei.
const FALLBACK_BENEFITS: Record<string, string[]> = {
  basic: ['Agendamentos ilimitados', '1 profissional', 'Página de agendamento + QR Code pra divulgar'],
  starter: [
    'Tudo do Essencial +',
    'Até 3 profissionais com agendas separadas',
    'IA no WhatsApp atende sozinha (100 respostas por dia)',
  ],
  growth: [
    'Tudo do Starter +',
    'Até 10 profissionais com agendas separadas',
    'IA no WhatsApp 24/7 (500 respostas por dia — 5× o Starter)',
  ],
};

const STEP_ORDER = ['data', 'payment', 'review'] as const;
type Step = (typeof STEP_ORDER)[number];
type Stage = 'plans' | 'checkout' | 'pix' | 'card' | 'success';
type Method = 'pix' | 'card';
type Direction = 'forward' | 'back';
type PixState = 'awaiting' | 'qr-expired' | 'qr-unavailable' | 'closed';
type CardState = 'confirming' | 'slow' | 'declined';
type CopyState = 'idle' | 'copied' | 'failed';

const STEP_LABELS: Record<Step, string> = { data: 'Dados', payment: 'Pagamento', review: 'Revisão' };
const STEP_HEADINGS: Record<Step, string> = {
  data: 'rc-data-title',
  payment: 'rc-payment-title',
  review: 'rc-review-title',
};

type DataField = 'name' | 'email' | 'document';
type CardField = 'number' | 'holder' | 'expiry' | 'cvv' | 'phone' | 'postal' | 'address';
// Ordem = ordem na tela: o foco vai pro PRIMEIRO campo com erro.
const DATA_FIELD_IDS: Record<DataField, string> = {
  name: 'rc-name',
  email: 'rc-email',
  document: 'rc-document',
};
const CARD_FIELD_IDS: Record<CardField, string> = {
  number: 'rc-card-number',
  holder: 'rc-card-name',
  expiry: 'rc-card-expiry',
  cvv: 'rc-card-cvv',
  phone: 'rc-card-phone',
  postal: 'rc-card-postal',
  address: 'rc-card-address',
};

function firstErrorId<K extends string>(errors: Record<K, string>, ids: Record<K, string>): string | undefined {
  const key = (Object.keys(ids) as K[]).find((k) => errors[k]);
  return key ? ids[key] : undefined;
}

interface PlanView {
  slug: string;
  name: string;
  monthly: number;
  annual: number | null;
  benefits: string[];
  homeUrl: string | null;
}

interface PixView {
  qr: string | null;
  payload: string | null;
  expiresAt: number | null;
  amount: number | null;
}

interface PaymentWatch {
  chargeId: string;
  kind: Method;
  /** Muda a cada "Verificar de novo" — recomeça o acompanhamento. */
  round: number;
}

function toPlanView(plan: PublicPlanInfo): PlanView | null {
  const monthly = toAmount(plan.monthly_price);
  if (monthly === null) return null;
  const highlights = (plan.highlights ?? []).filter((h) => h.trim().length > 0);
  return {
    slug: plan.slug,
    name: displayPlanName(plan.name),
    monthly,
    annual: toAmount(plan.annual_price),
    benefits: highlights.length > 0 ? highlights : (FALLBACK_BENEFITS[plan.slug] ?? []),
    homeUrl: plan.saas?.url ?? null,
  };
}

export function ReserveiCheckout(): JSX.Element {
  const entry = useCheckoutEntry();
  // Capturado na montagem: o intent salvo é apagado depois do 1º envio, e a
  // nova tentativa (cartão recusado) precisa do MESMO passe.
  const [handoffToken] = useState(entry.handoffToken);
  const [redirectUrl] = useState(entry.redirectUrl);
  const reducedMotion = useReducedMotion() !== false;
  const isDesktop = useIsDesktop();

  const plansQuery = useQuery({
    queryKey: RESERVEI_PLANS_QUERY_KEY,
    queryFn: () => fetchPublicPlans(SAAS),
    retry: false,
    staleTime: 60_000,
    // O catálogo não muda no meio de uma compra — recarregar ao voltar pra aba
    // só abriria espaço pra uma falha de rede atrapalhar quem está pagando.
    refetchOnWindowFocus: false,
  });
  const plans = useMemo(
    () => (plansQuery.data ?? []).map(toPlanView).filter((p): p is PlanView => p !== null),
    [plansQuery.data],
  );

  const [planSlug, setPlanSlug] = useState(entry.slug ?? '');
  const [draftPlanSlug, setDraftPlanSlug] = useState(entry.slug ?? '');
  const [cycle, setCycle] = useState<CheckoutBillingCycle>(entry.initialCycle);
  const [stage, setStage] = useState<Stage>('checkout');
  const [step, setStep] = useState<Step>('data');
  const [direction, setDirection] = useState<Direction>('forward');
  const [method, setMethod] = useState<Method>('pix');

  const [name, setName] = useState(entry.initialName);
  const [email, setEmail] = useState(entry.initialEmail);
  const [documentInput, setDocumentInput] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [phone, setPhone] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showDataErrors, setShowDataErrors] = useState(false);
  const [showCardErrors, setShowCardErrors] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState<CouponValidationResult | null>(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [formError, setFormError] = useState<CheckoutErrorInfo | null>(null);
  const [blocked, setBlocked] = useState<CheckoutErrorInfo | null>(null);

  const [pix, setPix] = useState<PixView | null>(null);
  const [pixState, setPixState] = useState<PixState>('awaiting');
  const [pixLoading, setPixLoading] = useState(false);
  const [pixSecondsLeft, setPixSecondsLeft] = useState(0);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [cardState, setCardState] = useState<CardState>('confirming');
  const [watch, setWatch] = useState<PaymentWatch | null>(null);
  const [pollStalled, setPollStalled] = useState(false);
  const [wasTrial, setWasTrial] = useState(false);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const submittingRef = useRef(false);
  const aliveRef = useRef(true);
  const couponRequestRef = useRef(0);
  const copyRequestRef = useRef(0);
  const pendingFocusRef = useRef<string | null>(null);

  const plan = plans.find((p) => p.slug === planSlug) ?? null;
  const draftPlan = plans.find((p) => p.slug === draftPlanSlug) ?? plan;
  const homeUrl = plans[0]?.homeUrl ?? RESERVEI_FALLBACK_URL;
  const backTarget = resolveSuccessTarget(homeUrl, redirectUrl, SAAS) ?? homeUrl;
  const helpUrl = safeUrl('/suporte', homeUrl);

  const hasAnnual = plan?.annual != null;
  const effectiveCycle: CheckoutBillingCycle = hasAnnual && cycle === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const isAnnual = effectiveCycle === 'YEARLY';
  const price = plan ? (isAnnual && plan.annual != null ? plan.annual : plan.monthly) : 0;
  const couponApplied = coupon?.valid === true ? coupon : null;
  const firstCharge = couponApplied ? couponApplied.final_price : price;
  const cycleLabel = isAnnual ? 'Anual' : 'Mensal';
  const methodLabel = method === 'pix' ? 'Pix' : 'Cartão de crédito';
  const renewalText = `A assinatura renova sozinha: ${formatPrice(price)} por ${isAnnual ? 'ano' : 'mês'}, até você cancelar. Cancele quando quiser.`;

  const documentValue = normalizeDocument(documentInput);
  const phoneDigits = onlyDigits(phone);
  const cardDigits = onlyDigits(cardNumber);
  const dataErrors: Record<DataField, string> = {
    name: name.trim().length < 3 ? 'Informe seu nome completo.' : '',
    email: !EMAIL_RE.test(email.trim()) ? 'Informe um e-mail válido.' : '',
    document: !isValidCpfOrCnpj(documentValue) ? 'CPF ou CNPJ inválido. Confira os números.' : '',
  };
  const cardErrors: Record<CardField, string> = {
    number: cardDigits.length < 13 ? 'Número do cartão incompleto.' : '',
    holder: cardName.trim().length < 3 ? 'Informe o nome como está no cartão.' : '',
    expiry: !isExpiryValid(cardExpiry) ? 'Validade inválida (MM/AA).' : '',
    cvv: cardCvv.length < 3 ? 'CVV com 3 ou 4 números.' : '',
    phone: phoneDigits.length < 10 ? 'Celular com DDD (10 ou 11 números).' : '',
    postal: onlyDigits(postalCode).length !== 8 ? 'CEP com 8 números.' : '',
    address: addressNumber.trim().length === 0 ? 'Informe o número do endereço.' : '',
  };
  const firstDataErrorId = firstErrorId(dataErrors, DATA_FIELD_IDS);
  const firstCardErrorId = firstErrorId(cardErrors, CARD_FIELD_IDS);
  const shownDataError = (key: DataField): string => (showDataErrors ? dataErrors[key] : '');
  const shownCardError = (key: CardField): string => (showCardErrors ? cardErrors[key] : '');

  useCheckoutPageMeta('Assinar o Reservei');

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      couponRequestRef.current += 1;
      copyRequestRef.current += 1;
    };
  }, []);

  // Altura real da barra de ação → espaço no fim da página (a barra é fixa).
  useEffect(() => {
    const dock = dockRef.current;
    const root = rootRef.current;
    if (!dock || !root || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() =>
      root.style.setProperty('--reservei-dock-height', `${dock.offsetHeight}px`),
    );
    observer.observe(dock);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--reservei-dock-height');
    };
  }, [stage, step, dockOpen, blocked, plansQuery.isLoading, isDesktop]);

  // Foco acompanha a troca de etapa (leitor de tela e teclado).
  useEffect(() => {
    const id = pendingFocusRef.current;
    if (!id) return;
    pendingFocusRef.current = null;
    document.getElementById(id)?.focus();
  });

  // Prazo do QR — o relógio é o do servidor (pix_expiration), nunca um fixo do front.
  const pixExpiresAt = pix?.expiresAt ?? null;
  useEffect(() => {
    if (stage !== 'pix' || pixState !== 'awaiting' || pixExpiresAt === null) return undefined;
    const tick = (): void => {
      const left = Math.max(0, Math.ceil((pixExpiresAt - Date.now()) / 1000));
      setPixSecondsLeft(left);
      if (left === 0) {
        copyRequestRef.current += 1;
        setCopyState('idle');
        setPixState('qr-expired');
      }
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [stage, pixState, pixExpiresAt]);

  // Acompanha a cobrança até paga/encerrada. Cadeia de setTimeout (não
  // setInterval) pra nunca ter duas perguntas ao mesmo tempo.
  useEffect(() => {
    if (!watch) return undefined;
    let cancelled = false;
    let attempts = 0;
    let consecutiveErrors = 0;
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      attempts += 1;
      try {
        const status = await fetchPaymentStatus(watch.chargeId);
        if (cancelled) return;
        consecutiveErrors = 0;
        if (status.paid) {
          finishSuccess(false);
          return;
        }
        if (TERMINAL_UNPAID_STATUSES.has(status.status)) {
          if (watch.kind === 'pix') setPixState('closed');
          else setCardState('declined');
          return;
        }
      } catch {
        if (cancelled) return;
        consecutiveErrors += 1;
        if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) {
          setPollStalled(true);
          return;
        }
      }
      if (attempts >= PAYMENT_POLL_MAX_ATTEMPTS) {
        setPollStalled(true);
        return;
      }
      if (watch.kind === 'card' && attempts === CARD_SLOW_AFTER_ATTEMPTS) {
        setCardState((current) => (current === 'confirming' ? 'slow' : current));
      }
      timer = window.setTimeout(() => void tick(), PAYMENT_POLL_INTERVAL_MS);
    };

    timer = window.setTimeout(() => void tick(), PAYMENT_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Recomeça só quando `watch` muda (nova cobrança ou "Verificar de novo").
  }, [watch]);

  function finishSuccess(trial: boolean): void {
    clearReserveiCheckoutIntent();
    setWatch(null);
    setPollStalled(false);
    setWasTrial(trial);
    setDirection('forward');
    setStage('success');
    pendingFocusRef.current = 'rc-success-title';
  }

  function resetCoupon(message: string): void {
    couponRequestRef.current += 1;
    setCouponLoading(false);
    if (coupon?.valid) setCouponMessage(message);
    setCoupon(null);
  }

  function goToStep(target: Step): void {
    if (target === step) return;
    setDirection(STEP_ORDER.indexOf(target) > STEP_ORDER.indexOf(step) ? 'forward' : 'back');
    setStep(target);
    pendingFocusRef.current = STEP_HEADINGS[target];
  }

  function chooseCycle(next: CheckoutBillingCycle): void {
    if (locked || submitting || next === effectiveCycle) return;
    setCycle(next);
    resetCoupon('O valor mudou — aplique o cupom de novo.');
  }

  function chooseMethod(next: Method): void {
    if (locked || submitting) return;
    setMethod(next);
    setFormError(null);
  }

  function openPlans(): void {
    if (locked || submitting) return;
    setDraftPlanSlug(planSlug);
    setSummaryOpen(false);
    setDirection('back');
    setStage('plans');
    pendingFocusRef.current = 'rc-plans-title';
  }

  function closePlans(apply: boolean): void {
    if (apply && draftPlanSlug !== planSlug) {
      setPlanSlug(draftPlanSlug);
      resetCoupon('O plano mudou — aplique o cupom de novo.');
      setStep('data');
    }
    setDirection('forward');
    setStage('checkout');
    pendingFocusRef.current = 'rc-checkout-title';
  }

  async function applyCoupon(): Promise<void> {
    if (!plan || locked || couponLoading) return;
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      setCouponMessage('Digite o código do cupom.');
      return;
    }
    const requestId = couponRequestRef.current + 1;
    couponRequestRef.current = requestId;
    setCouponLoading(true);
    try {
      const result = await validateCoupon({
        code,
        plan_slug: plan.slug,
        saas_origin: SAAS,
        billing_cycle: isAnnual ? 'anual' : 'mensal',
        email: EMAIL_RE.test(email.trim()) ? email.trim() : undefined,
      });
      if (couponRequestRef.current !== requestId) return;
      if (result.valid) {
        setCoupon(result);
        setCouponInput(result.code);
        setCouponMessage(
          `${result.code} aplicado: ${formatPrice(result.discount_amount)} de desconto na primeira cobrança (${formatPrice(result.final_price)}).`,
        );
      } else {
        setCoupon(null);
        setCouponMessage(result.message);
      }
    } catch {
      if (couponRequestRef.current !== requestId) return;
      setCoupon(null);
      setCouponMessage('Não foi possível validar o cupom agora. Tente de novo.');
    } finally {
      if (couponRequestRef.current === requestId) setCouponLoading(false);
    }
  }

  function removeCoupon(): void {
    if (locked) return;
    couponRequestRef.current += 1;
    setCouponLoading(false);
    setCoupon(null);
    setCouponInput('');
    setCouponMessage('Cupom removido.');
  }

  async function loadPix(chargeId: string): Promise<void> {
    setPixLoading(true);
    copyRequestRef.current += 1;
    setCopyState('idle');
    try {
      const details = await fetchPaymentDetails(chargeId);
      if (!aliveRef.current) return;
      if (PAID_STATUSES.has(details.status)) {
        finishSuccess(false);
        return;
      }
      if (TERMINAL_UNPAID_STATUSES.has(details.status)) {
        setPixState('closed');
        return;
      }
      const qr = details.pix_qr_image
        ? details.pix_qr_image.startsWith('data:')
          ? details.pix_qr_image
          : `data:image/png;base64,${details.pix_qr_image}`
        : null;
      const expiresAt = parsePixExpiration(details.pix_expiration);
      setPix({ qr, payload: details.pix_payload, expiresAt, amount: toAmount(details.amount) });
      if (!qr && !details.pix_payload) setPixState('qr-unavailable');
      // Prazo do servidor já passou: gerar de novo devolveria o mesmo QR vencido.
      else if (expiresAt !== null && expiresAt <= Date.now()) setPixState('closed');
      else setPixState('awaiting');
    } catch {
      if (aliveRef.current) setPixState('qr-unavailable');
    } finally {
      if (aliveRef.current) setPixLoading(false);
    }
  }

  async function copyPixCode(): Promise<void> {
    const code = pix?.payload;
    if (!code) return;
    const requestId = copyRequestRef.current + 1;
    copyRequestRef.current = requestId;
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    try {
      if (!clipboard?.writeText) throw new Error('clipboard indisponível');
      await clipboard.writeText(code);
      if (aliveRef.current && copyRequestRef.current === requestId) setCopyState('copied');
    } catch {
      if (aliveRef.current && copyRequestRef.current === requestId) setCopyState('failed');
    }
  }

  function restartWatch(): void {
    if (!watch) return;
    setPollStalled(false);
    setWatch({ ...watch, round: watch.round + 1 });
  }

  async function submit(): Promise<void> {
    if (!plan || submittingRef.current) return;
    // O stepper deixa voltar e editar — revalida tudo na hora de enviar.
    if (firstDataErrorId) {
      setShowDataErrors(true);
      goToStep('data');
      pendingFocusRef.current = firstDataErrorId;
      return;
    }
    if (method === 'card' && firstCardErrorId) {
      setShowCardErrors(true);
      goToStep('payment');
      pendingFocusRef.current = firstCardErrorId;
      return;
    }
    if (!termsAccepted) {
      setShowTermsError(true);
      document.getElementById('rc-terms')?.focus();
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setFormError(null);

    const [expiryMonth = '', expiryYear = ''] = cardExpiry.split('/');
    const payload: PublicCheckoutPayload = {
      plan_slug: plan.slug,
      email: email.trim(),
      name: name.trim(),
      cpf_cnpj: documentValue,
      phone: method === 'card' ? phoneDigits : undefined,
      payment_method: method === 'pix' ? 'pix' : 'credit_card',
      billing_cycle: isAnnual ? 'anual' : 'mensal',
      saas_origin: SAAS,
      handoff_token: handoffToken,
      coupon_code: couponApplied?.code,
      terms_accepted: true,
      terms_version: CHECKOUT_TERMS_VERSION,
      credit_card:
        method === 'card'
          ? {
              number: cardDigits,
              holder_name: cardName.trim(),
              expiry_month: expiryMonth,
              expiry_year: `20${expiryYear}`,
              ccv: cardCvv,
              postal_code: onlyDigits(postalCode),
              address_number: addressNumber.trim(),
            }
          : undefined,
    };

    try {
      const result = await submitPublicCheckout(payload);
      if (!aliveRef.current) return;
      clearReserveiCheckoutIntent();
      setLocked(true);
      if (result.is_trial || result.status === 'em_teste') {
        finishSuccess(true);
        return;
      }
      if (!result.charge_id) {
        if (result.payment_url) {
          window.location.assign(result.payment_url);
          return;
        }
        finishSuccess(false);
        return;
      }
      setPollStalled(false);
      setDirection('forward');
      if (method === 'pix') {
        setPix(null);
        setPixState('awaiting');
        setStage('pix');
        pendingFocusRef.current = 'rc-pix-title';
        setWatch({ chargeId: result.charge_id, kind: 'pix', round: 0 });
        await loadPix(result.charge_id);
      } else {
        setCardState('confirming');
        setStage('card');
        pendingFocusRef.current = 'rc-card-title';
        setWatch({ chargeId: result.charge_id, kind: 'card', round: 0 });
      }
    } catch (err) {
      if (!aliveRef.current) return;
      const info = classifyCheckoutError(err);
      if (LOCKING_KINDS.has(info.kind)) setLocked(true);
      if (info.kind === 'coupon') {
        setCoupon(null);
        setCouponMessage(info.message);
      }
      if (BLOCKING_KINDS.has(info.kind)) {
        setBlocked(info);
      } else {
        setFormError(info);
        pendingFocusRef.current = 'rc-form-error';
      }
    } finally {
      submittingRef.current = false;
      if (aliveRef.current) setSubmitting(false);
    }
  }

  function advance(): void {
    if (submitting) return;
    if (stage === 'plans') {
      closePlans(true);
      return;
    }
    if (stage === 'pix') {
      if (pixState === 'closed') window.location.assign(backTarget);
      else if (pollStalled) restartWatch();
      else if (pixState === 'awaiting') void copyPixCode();
      else if (watch && !pixLoading) void loadPix(watch.chargeId);
      return;
    }
    if (stage === 'card') {
      if (cardState === 'declined') window.location.assign(backTarget);
      else if (pollStalled) restartWatch();
      return;
    }
    if (stage === 'success') {
      window.location.assign(backTarget);
      return;
    }
    if (step === 'data') {
      setShowDataErrors(true);
      if (firstDataErrorId) {
        document.getElementById(firstDataErrorId)?.focus();
        return;
      }
      goToStep('payment');
      return;
    }
    if (step === 'payment') {
      if (method === 'card') {
        setShowCardErrors(true);
        if (firstCardErrorId) {
          document.getElementById(firstCardErrorId)?.focus();
          return;
        }
      }
      goToStep('review');
      return;
    }
    void submit();
  }

  function handleFormSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    advance();
  }

  const dockCta = ((): { label: string; disabled: boolean } => {
    if (stage === 'plans') return { label: `Usar plano ${draftPlan?.name ?? ''}`.trim(), disabled: false };
    if (stage === 'pix') {
      if (pixState === 'closed') return { label: 'Voltar ao Reservei', disabled: false };
      if (pollStalled) return { label: 'Verificar pagamento', disabled: false };
      if (pixLoading) return { label: 'Gerando Pix…', disabled: true };
      if (pixState === 'awaiting') {
        return { label: copyState === 'copied' ? 'Código copiado' : 'Copiar código Pix', disabled: !pix?.payload };
      }
      return { label: pixState === 'qr-expired' ? 'Gerar novo código' : 'Carregar o Pix de novo', disabled: false };
    }
    if (stage === 'card') {
      if (cardState === 'declined') return { label: 'Voltar ao Reservei', disabled: false };
      if (pollStalled) return { label: 'Verificar pagamento', disabled: false };
      return { label: 'Confirmando…', disabled: true };
    }
    if (stage === 'success') return { label: 'Voltar para o Reservei', disabled: false };
    if (step === 'data') return { label: 'Continuar para pagamento', disabled: false };
    if (step === 'payment') return { label: 'Revisar pedido', disabled: false };
    if (submitting) return { label: 'Enviando…', disabled: true };
    return { label: method === 'pix' ? 'Gerar Pix' : `Pagar ${formatPrice(firstCharge)}`, disabled: false };
  })();

  const enter = (dir: Direction): false | { opacity: number; x: number } =>
    reducedMotion ? false : { opacity: 0, x: dir === 'forward' ? 40 : -40 };
  const enterTransition = reducedMotion ? { duration: 0 } : { duration: 0.32, ease: EASE };

  // ---------- Telas que encerram a compra aqui ----------

  const blockedScreen = (title: string, message: string, action?: ReactNode): JSX.Element => (
    <div className="reservei-checkout" ref={rootRef}>
      <Header homeUrl={homeUrl} backTarget={backTarget} helpUrl={helpUrl} />
      <main className="reservei-checkout__main">
        <section className="reservei-checkout__blocked" aria-labelledby="rc-blocked-title">
          <h1 id="rc-blocked-title">{title}</h1>
          <p>{message}</p>
          {action ?? (
            <a className="reservei-checkout__primary-link" href={backTarget}>
              Voltar ao Reservei
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );

  if (!handoffToken) {
    return blockedScreen(
      'Comece a compra pelo Reservei',
      'Abra o Reservei, vá em Planos e toque em Assinar. Assim a assinatura fica ligada à sua conta. Nada foi cobrado.',
    );
  }
  if (blocked) {
    return blockedScreen(BLOCKED_TITLES[blocked.kind] ?? 'Não deu para concluir', blocked.message);
  }
  if (plansQuery.isLoading) {
    return (
      <div className="reservei-checkout" ref={rootRef}>
        <Header homeUrl={homeUrl} backTarget={backTarget} helpUrl={helpUrl} />
        <main className="reservei-checkout__main">
          <div className="reservei-checkout__loading" role="status" aria-label="Carregando o checkout">
            <span className="reservei-checkout__skeleton reservei-checkout__skeleton--title" />
            <span className="reservei-checkout__skeleton" />
            <span className="reservei-checkout__skeleton reservei-checkout__skeleton--block" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  // Só sem dado nenhum: uma recarga que falha com a lista já na tela mantém a
  // lista (react-query guarda o dado) — trocar o checkout pela tela de erro
  // no meio da compra seria pior que mostrar o catálogo de um minuto atrás.
  if (plansQuery.isError && !plansQuery.data) {
    return blockedScreen(
      'Não conseguimos carregar os planos',
      'Confira sua internet e tente de novo. Nada foi cobrado.',
      <button type="button" className="reservei-checkout__primary-link" onClick={() => void plansQuery.refetch()}>
        Tentar de novo
      </button>,
    );
  }
  if (!plan) {
    return blockedScreen(
      'Plano indisponível',
      'Este plano não está disponível agora. Volte ao Reservei e escolha um plano na página de Planos.',
    );
  }

  // ---------- Checkout ----------

  const currentStepIndex = STEP_ORDER.indexOf(step);
  const visibleStepIndex = stage === 'checkout' ? currentStepIndex : STEP_ORDER.length - 1;
  const stepProgress = visibleStepIndex / (STEP_ORDER.length - 1);
  const stepperLocked = stage !== 'checkout' || submitting;
  const lockedNote =
    'Plano, ciclo, forma de pagamento, CPF/CNPJ, e-mail e cupom ficaram fixos depois do primeiro envio. Para mudar algum deles, volte ao Reservei e toque em Assinar de novo.';

  const pixStatusLabel =
    pixState === 'closed' ? 'Pix vencido' : pixState === 'qr-expired' ? 'Código expirado' : 'Aguardando pagamento';
  const pixTimerLabel =
    pixExpiresAt === null
      ? null
      : pixSecondsLeft > 3600
        ? `Vale até ${formatBRDateTime(new Date(pixExpiresAt))}`
        : formatCountdown(pixSecondsLeft);
  const pixAmount = pix?.amount ?? firstCharge;

  const cardCopy: Record<CardState, { title: string; text: string }> = {
    confirming: {
      title: 'Confirmando o pagamento',
      text: 'Estamos falando com o banco. Não feche esta página e não pague de novo.',
    },
    slow: {
      title: 'Ainda confirmando',
      text: 'O banco está demorando mais que o normal. Não pague de novo: assim que confirmar, seu plano é liberado no Reservei.',
    },
    declined: {
      title: 'Pagamento não aprovado',
      text: 'O banco recusou o pagamento. Volte ao Reservei e toque em Assinar de novo para tentar outro cartão. Nada foi cobrado.',
    },
  };
  const cardView = pollStalled && cardState !== 'declined'
    ? {
        title: 'Não conseguimos confirmar agora',
        text: 'Se o pagamento for aprovado, seu plano é liberado no Reservei sozinho. Você pode verificar de novo.',
      }
    : cardCopy[cardState];

  // Celular/tablet: etapas no topo (desenho aprovado da prévia). Computador:
  // etapas na grade, em cima do formulário, com a linha do tamanho do card.
  const stepper =
    stage === 'plans' ? null : (
      <nav className="reservei-checkout__stepper" aria-label="Etapas da compra">
        {STEP_ORDER.map((stepName, index) => {
          const isCurrent = index === visibleStepIndex;
          const isPast = index < visibleStepIndex;
          return (
            <button
              type="button"
              key={stepName}
              aria-current={isCurrent ? 'step' : undefined}
              disabled={stepperLocked || index > visibleStepIndex}
              className={isCurrent ? 'is-current' : isPast ? 'is-past' : ''}
              onClick={() => goToStep(stepName)}
            >
              <span>{index + 1}</span>
              {STEP_LABELS[stepName]}
            </button>
          );
        })}
        <span className="reservei-checkout__stepper-track" aria-hidden="true">
          <motion.span
            className="reservei-checkout__stepper-progress"
            initial={false}
            animate={{ width: `${stepProgress * 100}%` }}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.32, ease: EASE }}
          />
        </span>
      </nav>
    );

  // Total + botão principal: na barra fixa (celular/tablet) OU no pé do card
  // (computador) — nunca os dois, pra não existir dois "Pagar" na tela.
  const actionTotal = (
    <div className="reservei-checkout__dock-total">
      <div className="reservei-checkout__dock-total-value">
        {stage === 'plans' ? (
          <>
            <span>Plano {draftPlan?.name}</span>
            <small>confirme para aplicar</small>
          </>
        ) : (
          <>
            <span>
              {cycleLabel} · {formatPrice(stage === 'pix' ? pixAmount : firstCharge)}
            </span>
            <small>{couponApplied ? 'primeira cobrança com cupom' : 'primeira cobrança'}</small>
          </>
        )}
      </div>
    </div>
  );
  const primaryAction = (
    <button
      type="button"
      className="reservei-checkout__dock-cta"
      disabled={dockCta.disabled}
      aria-busy={submitting || undefined}
      onClick={(event) => {
        if (event.detail > 1) return;
        advance();
      }}
    >
      {submitting && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
      {dockCta.label}
      {!submitting && <ArrowRight size={18} aria-hidden="true" />}
    </button>
  );
  const inlineActions = isDesktop ? (
    <div className="reservei-checkout__inline-actions">
      {actionTotal}
      {primaryAction}
    </div>
  ) : null;

  return (
    <div className="reservei-checkout" ref={rootRef}>
      <Header homeUrl={homeUrl} backTarget={backTarget} helpUrl={helpUrl} />

      <main className="reservei-checkout__main">
        {!isDesktop && stepper}

        {stage === 'plans' ? (
          <motion.section
            key="plans"
            className="reservei-checkout__plan-selection"
            initial={enter(direction)}
            animate={{ opacity: 1, x: 0 }}
            transition={enterTransition}
            aria-labelledby="rc-plans-title"
          >
            <h1 id="rc-plans-title" tabIndex={-1}>
              Escolha seu plano
            </h1>
            <p className="reservei-checkout__plan-selection-copy">
              Selecione uma opção e confirme para voltar ao checkout.
            </p>
            <button type="button" className="reservei-checkout__plan-cancel" onClick={() => closePlans(false)}>
              Cancelar e voltar ao checkout
            </button>
            <div className="reservei-checkout__plan-cards" role="group" aria-label="Planos disponíveis">
              {plans.map((option) => (
                <button
                  type="button"
                  key={option.slug}
                  className={`reservei-checkout__plan-card${option.slug === draftPlanSlug ? ' is-selected' : ''}`}
                  aria-pressed={option.slug === draftPlanSlug}
                  onClick={() => setDraftPlanSlug(option.slug)}
                >
                  {option.slug === draftPlanSlug && <SelectionHighlight reducedMotion={reducedMotion} />}
                  <span className="reservei-checkout__plan-card-name">{option.name}</span>
                  <Price value={option.monthly} suffix="/mês" />
                  {option.annual != null && (
                    <span className="reservei-checkout__plan-card-annual">{formatPrice(option.annual)}/ano</span>
                  )}
                  {(option.benefits[1] ?? option.benefits[0]) && (
                    <span className="reservei-checkout__plan-card-benefit">
                      {option.benefits[1] ?? option.benefits[0]}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {inlineActions}
            <p className="reservei-checkout__plan-help">Dá para trocar de plano depois, pelo próprio Reservei.</p>
          </motion.section>
        ) : (
          <motion.div
            key="checkout"
            className="reservei-checkout__checkout-view"
            initial={enter(direction)}
            animate={{ opacity: 1, x: 0 }}
            transition={enterTransition}
          >
            <div className="reservei-checkout__layout">
              <section className="reservei-checkout__intro" aria-labelledby="rc-checkout-title">
                <h1 id="rc-checkout-title" tabIndex={-1} aria-label="Assine o Reservei">
                  <span>Assine o</span>
                  <span>Reservei</span>
                </h1>
                <p>Confira seus dados para continuar.</p>
              </section>

              {isDesktop && stepper}

              <aside className="reservei-checkout__summary" aria-label="Resumo da assinatura">
                <div className={`reservei-checkout__summary-panel${summaryOpen ? ' is-open' : ''}`}>
                  <p className="reservei-checkout__eyebrow">Resumo</p>
                  <div className="reservei-checkout__summary-heading">
                    <span className="reservei-checkout__calendar" aria-hidden="true">
                      <img src="/checkout/reservei/calendario.png" alt="" />
                    </span>
                    <div className="reservei-checkout__summary-details">
                      <h2>Plano {plan.name}</h2>
                      <span className="reservei-checkout__summary-cycle-label">{cycleLabel}</span>
                    </div>
                    <div className="reservei-checkout__summary-price">
                      <Price value={price} suffix={isAnnual ? '/ano' : '/mês'} />
                      {isAnnual && (
                        <p className="reservei-checkout__annual-note">{formatPrice(price / 12)}/mês equivalente</p>
                      )}
                    </div>
                  </div>

                  {!locked && (
                    <button type="button" className="reservei-checkout__plan-change" onClick={openPlans}>
                      Trocar plano
                    </button>
                  )}

                  <div className="reservei-checkout__summary-expanded">
                    <div className="reservei-checkout__summary-expanded-content">
                      {hasAnnual && plan.annual != null && (
                        <div className="reservei-checkout__cycle-switch" role="group" aria-label="Escolha o ciclo">
                          <button
                            type="button"
                            className={!isAnnual ? 'is-selected' : ''}
                            aria-pressed={!isAnnual}
                            disabled={locked}
                            onClick={() => chooseCycle('MONTHLY')}
                          >
                            {!isAnnual && <SelectionHighlight reducedMotion={reducedMotion} />}
                            Mensal · {formatPrice(plan.monthly)}
                          </button>
                          <button
                            type="button"
                            className={isAnnual ? 'is-selected' : ''}
                            aria-pressed={isAnnual}
                            disabled={locked}
                            onClick={() => chooseCycle('YEARLY')}
                          >
                            {isAnnual && <SelectionHighlight reducedMotion={reducedMotion} />}
                            Anual · {formatPrice(plan.annual)}
                          </button>
                        </div>
                      )}

                      {plan.benefits.length > 0 && (
                        <div className="reservei-checkout__benefits" aria-label="Benefícios do plano">
                          {plan.benefits.map((benefit) => (
                            <div key={benefit}>
                              <Check size={18} aria-hidden="true" />
                              <span>{benefit}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="reservei-checkout__summary-toggle"
                    aria-expanded={summaryOpen}
                    onClick={() => setSummaryOpen((open) => !open)}
                  >
                    <span>{summaryOpen ? 'Ocultar ciclo e benefícios' : 'Ver ciclo e benefícios'}</span>
                    <ChevronDown className={summaryOpen ? 'is-open' : ''} size={18} aria-hidden="true" />
                  </button>
                </div>

                <div className="reservei-checkout__support">
                  <img src="/checkout/reservei/aceno.png" alt="" />
                  <p>
                    <span>Dúvidas na compra?</span>
                    <span>
                      <a className="reservei-checkout__inline-link" href={helpUrl} target="_blank" rel="noopener noreferrer">
                        Fale com o suporte do Reservei
                      </a>
                    </span>
                  </p>
                </div>
              </aside>

              <section className="reservei-checkout__form-column" aria-label="Dados e pagamento">
                {stage === 'checkout' && (
                  <motion.div
                    key={`step-${step}`}
                    className="reservei-checkout__animated-scene"
                    initial={enter(direction)}
                    animate={{ opacity: 1, x: 0 }}
                    transition={enterTransition}
                  >
                    <form className="reservei-checkout__form" onSubmit={handleFormSubmit} noValidate>
                      {step === 'data' && (
                        <div className="reservei-checkout__animated-step">
                          <h2 id="rc-data-title" tabIndex={-1}>
                            Seus dados
                          </h2>
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-name">Nome completo</label>
                            <input
                              id="rc-name"
                              autoComplete="name"
                              value={name}
                              aria-invalid={Boolean(shownDataError('name'))}
                              aria-describedby={shownDataError('name') ? 'rc-name-error' : undefined}
                              onChange={(event) => setName(event.target.value)}
                            />
                            <FieldError id="rc-name-error" message={shownDataError('name')} />
                          </div>
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-email">E-mail</label>
                            <input
                              id="rc-email"
                              type="email"
                              autoComplete="email"
                              inputMode="email"
                              value={email}
                              disabled={locked}
                              aria-invalid={Boolean(shownDataError('email'))}
                              aria-describedby={shownDataError('email') ? 'rc-email-error' : 'rc-email-hint'}
                              onChange={(event) => setEmail(event.target.value)}
                            />
                            {shownDataError('email') ? (
                              <FieldError id="rc-email-error" message={shownDataError('email')} />
                            ) : (
                              <span className="reservei-checkout__field-hint" id="rc-email-hint">
                                Os recibos e avisos da assinatura chegam aqui.
                              </span>
                            )}
                          </div>
                          <div className="reservei-checkout__field">
                            <label htmlFor="rc-document">CPF ou CNPJ</label>
                            <input
                              id="rc-document"
                              inputMode="text"
                              autoComplete="off"
                              value={documentInput}
                              disabled={locked}
                              aria-invalid={Boolean(shownDataError('document'))}
                              aria-describedby={shownDataError('document') ? 'rc-document-error' : 'rc-document-hint'}
                              onChange={(event) => setDocumentInput(maskCpfCnpjAuto(event.target.value))}
                            />
                            {shownDataError('document') ? (
                              <FieldError id="rc-document-error" message={shownDataError('document')} />
                            ) : (
                              <span className="reservei-checkout__field-hint" id="rc-document-hint">
                                Vai na nota fiscal. No cartão, use o do titular do cartão.
                              </span>
                            )}
                          </div>
                          {locked && <p className="reservei-checkout__locked-note">{lockedNote}</p>}
                        </div>
                      )}

                      {step === 'payment' && (
                        <div className="reservei-checkout__animated-step">
                          <fieldset className="reservei-checkout__payment">
                            <legend id="rc-payment-title" tabIndex={-1}>
                              Pagamento
                            </legend>
                            <div className="reservei-checkout__payment-options">
                              <label className={method === 'pix' ? 'is-selected' : ''}>
                                {method === 'pix' && <SelectionHighlight reducedMotion={reducedMotion} />}
                                <input
                                  type="radio"
                                  name="rc-method"
                                  value="pix"
                                  checked={method === 'pix'}
                                  disabled={locked && method !== 'pix'}
                                  onChange={() => chooseMethod('pix')}
                                />
                                <span className="reservei-checkout__radio" aria-hidden="true" />
                                <PixIcon size={22} aria-label="" />
                                <span>Pix</span>
                              </label>
                              <label className={method === 'card' ? 'is-selected' : ''}>
                                {method === 'card' && <SelectionHighlight reducedMotion={reducedMotion} />}
                                <input
                                  aria-label="Cartão de crédito"
                                  type="radio"
                                  name="rc-method"
                                  value="card"
                                  checked={method === 'card'}
                                  disabled={locked && method !== 'card'}
                                  onChange={() => chooseMethod('card')}
                                />
                                <span className="reservei-checkout__radio" aria-hidden="true" />
                                <CreditCard size={22} aria-hidden="true" />
                                <span>
                                  <span className="reservei-checkout__method-label-full">Cartão de crédito</span>
                                  <span className="reservei-checkout__method-label-short">Cartão</span>
                                </span>
                              </label>
                            </div>
                            <p className="reservei-checkout__payment-helper">
                              {method === 'pix'
                                ? 'O QR Code aparece na próxima tela. A confirmação chega em segundos.'
                                : isAnnual
                                  ? 'Cobrança no cartão a cada ano, sem parcelamento.'
                                  : 'Cobrança no cartão todo mês, só o valor do mês.'}
                            </p>

                            {method === 'card' && (
                              <div className="reservei-checkout__card-form">
                                <div className="reservei-checkout__field">
                                  <label htmlFor="rc-card-number">Número do cartão</label>
                                  <input
                                    id="rc-card-number"
                                    inputMode="numeric"
                                    autoComplete="cc-number"
                                    value={cardNumber}
                                    aria-invalid={Boolean(shownCardError('number'))}
                                    aria-describedby={shownCardError('number') ? 'rc-card-number-error' : undefined}
                                    onChange={(event) => setCardNumber(formatCardNumber(event.target.value))}
                                  />
                                  <FieldError id="rc-card-number-error" message={shownCardError('number')} />
                                </div>
                                <div className="reservei-checkout__field">
                                  <label htmlFor="rc-card-name">Nome impresso no cartão</label>
                                  <input
                                    id="rc-card-name"
                                    autoComplete="cc-name"
                                    value={cardName}
                                    aria-invalid={Boolean(shownCardError('holder'))}
                                    aria-describedby={shownCardError('holder') ? 'rc-card-name-error' : undefined}
                                    onChange={(event) => setCardName(event.target.value)}
                                  />
                                  <FieldError id="rc-card-name-error" message={shownCardError('holder')} />
                                </div>
                                <div className="reservei-checkout__field-row">
                                  <div className="reservei-checkout__field">
                                    <label htmlFor="rc-card-expiry">Validade</label>
                                    <input
                                      id="rc-card-expiry"
                                      inputMode="numeric"
                                      autoComplete="cc-exp"
                                      placeholder="MM/AA"
                                      value={cardExpiry}
                                      aria-invalid={Boolean(shownCardError('expiry'))}
                                      aria-describedby={shownCardError('expiry') ? 'rc-card-expiry-error' : undefined}
                                      onChange={(event) => setCardExpiry(formatExpiry(event.target.value))}
                                    />
                                    <FieldError id="rc-card-expiry-error" message={shownCardError('expiry')} />
                                  </div>
                                  <div className="reservei-checkout__field">
                                    <label htmlFor="rc-card-cvv">CVV</label>
                                    <input
                                      id="rc-card-cvv"
                                      inputMode="numeric"
                                      autoComplete="cc-csc"
                                      value={cardCvv}
                                      aria-invalid={Boolean(shownCardError('cvv'))}
                                      aria-describedby={shownCardError('cvv') ? 'rc-card-cvv-error' : undefined}
                                      onChange={(event) => setCardCvv(onlyDigits(event.target.value).slice(0, 4))}
                                    />
                                    <FieldError id="rc-card-cvv-error" message={shownCardError('cvv')} />
                                  </div>
                                </div>
                                <div className="reservei-checkout__field">
                                  <label htmlFor="rc-card-phone">Celular do titular</label>
                                  <input
                                    id="rc-card-phone"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel-national"
                                    value={phone}
                                    aria-invalid={Boolean(shownCardError('phone'))}
                                    aria-describedby={shownCardError('phone') ? 'rc-card-phone-error' : undefined}
                                    onChange={(event) => setPhone(maskPhone(event.target.value))}
                                  />
                                  <FieldError id="rc-card-phone-error" message={shownCardError('phone')} />
                                </div>
                                <div className="reservei-checkout__field-row">
                                  <div className="reservei-checkout__field">
                                    <label htmlFor="rc-card-postal">CEP do titular</label>
                                    <input
                                      id="rc-card-postal"
                                      inputMode="numeric"
                                      autoComplete="postal-code"
                                      value={postalCode}
                                      aria-invalid={Boolean(shownCardError('postal'))}
                                      aria-describedby={shownCardError('postal') ? 'rc-card-postal-error' : undefined}
                                      onChange={(event) => setPostalCode(maskCep(event.target.value))}
                                    />
                                    <FieldError id="rc-card-postal-error" message={shownCardError('postal')} />
                                  </div>
                                  <div className="reservei-checkout__field">
                                    <label htmlFor="rc-card-address">Número do endereço</label>
                                    <input
                                      id="rc-card-address"
                                      autoComplete="off"
                                      maxLength={20}
                                      value={addressNumber}
                                      aria-invalid={Boolean(shownCardError('address'))}
                                      aria-describedby={shownCardError('address') ? 'rc-card-address-error' : undefined}
                                      onChange={(event) => setAddressNumber(event.target.value)}
                                    />
                                    <FieldError id="rc-card-address-error" message={shownCardError('address')} />
                                  </div>
                                </div>
                              </div>
                            )}
                          </fieldset>

                          <div className="reservei-checkout__coupon-slot">
                            {locked ? (
                              couponApplied && (
                                <p className="reservei-checkout__helper">Cupom {couponApplied.code} aplicado.</p>
                              )
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="reservei-checkout__coupon-trigger"
                                  aria-expanded={couponOpen}
                                  onClick={() => setCouponOpen((open) => !open)}
                                >
                                  <span>{couponOpen ? 'Ocultar cupom' : 'Adicionar cupom'}</span>
                                  <ChevronDown className={couponOpen ? 'is-open' : ''} size={16} aria-hidden="true" />
                                </button>
                                {couponOpen && (
                                  <motion.div
                                    className="reservei-checkout__coupon-form"
                                    initial={reducedMotion ? false : { opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    transition={reducedMotion ? { duration: 0 } : { duration: 0.26, ease: EASE }}
                                  >
                                    <label htmlFor="rc-coupon">Cupom</label>
                                    <div>
                                      <input
                                        id="rc-coupon"
                                        value={couponInput}
                                        maxLength={50}
                                        autoComplete="off"
                                        onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            void applyCoupon();
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => void applyCoupon()}
                                        disabled={couponLoading}
                                        aria-busy={couponLoading}
                                      >
                                        {couponLoading ? 'Validando…' : 'Aplicar'}
                                      </button>
                                    </div>
                                    <p className="reservei-checkout__helper">O desconto vale só para a primeira cobrança.</p>
                                    {couponMessage && (
                                      <p className="reservei-checkout__coupon-message" role="status">
                                        {couponMessage}
                                      </p>
                                    )}
                                    {couponApplied && (
                                      <button type="button" className="reservei-checkout__coupon-remove" onClick={removeCoupon}>
                                        Remover cupom
                                      </button>
                                    )}
                                  </motion.div>
                                )}
                              </>
                            )}
                          </div>
                          {locked && <p className="reservei-checkout__locked-note">{lockedNote}</p>}
                        </div>
                      )}

                      {step === 'review' && (
                        <div className="reservei-checkout__review-panel reservei-checkout__animated-step">
                          <h2 id="rc-review-title" tabIndex={-1}>
                            Revisão
                          </h2>
                          <p>
                            <strong>{plan.name}</strong> · {cycleLabel} ·{' '}
                            {method === 'pix' ? 'Pix' : `Cartão final ${cardDigits.slice(-4)}`}
                          </p>
                          <p>
                            {couponApplied
                              ? `Cupom ${couponApplied.code}: ${formatPrice(couponApplied.discount_amount)} de desconto na primeira cobrança.`
                              : 'Sem cupom.'}
                          </p>
                          <p>
                            <strong>Primeira cobrança: {formatPrice(firstCharge)}</strong>
                          </p>
                          <p className="reservei-checkout__renewal">{renewalText}</p>
                          <label className="reservei-checkout__consent" htmlFor="rc-terms">
                            <input
                              id="rc-terms"
                              type="checkbox"
                              checked={termsAccepted}
                              aria-invalid={showTermsError && !termsAccepted}
                              aria-describedby={showTermsError && !termsAccepted ? 'rc-terms-error' : undefined}
                              onChange={(event) => setTermsAccepted(event.target.checked)}
                            />
                            <span>
                              Li e aceito os{' '}
                              <a href="/termos-de-uso" target="_blank" rel="noopener noreferrer">
                                Termos de Uso
                              </a>{' '}
                              e a{' '}
                              <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
                                Política de Privacidade
                              </a>
                              .
                            </span>
                          </label>
                          <FieldError
                            id="rc-terms-error"
                            message={showTermsError && !termsAccepted ? 'Aceite os termos para continuar.' : ''}
                          />
                          {formError && (
                            <p className="reservei-checkout__alert" role="alert" id="rc-form-error" tabIndex={-1}>
                              {formError.message}
                            </p>
                          )}
                          {locked && <p className="reservei-checkout__locked-note">{lockedNote}</p>}
                        </div>
                      )}
                    </form>
                  </motion.div>
                )}

                {stage === 'pix' && (
                  <motion.div
                    key="pix"
                    className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--pix"
                    initial={enter('forward')}
                    animate={{ opacity: 1, x: 0 }}
                    transition={enterTransition}
                    aria-labelledby="rc-pix-title"
                  >
                    <div className="reservei-checkout__pix-shell">
                      <div className="reservei-checkout__pix-status-row">
                        <span className="reservei-checkout__pix-status">
                          <span className="reservei-checkout__pix-status-dot" aria-hidden="true" />
                          {pixStatusLabel}
                        </span>
                        {pixState === 'awaiting' && pixTimerLabel && (
                          <span className="reservei-checkout__pix-timer" role="timer" aria-live="off">
                            {pixTimerLabel}
                          </span>
                        )}
                      </div>

                      <div className="reservei-checkout__pix-title">
                        <PixIcon size={24} aria-label="" />
                        <h2 id="rc-pix-title" tabIndex={-1}>
                          {pixState === 'closed' ? 'Pix vencido' : pixState === 'qr-expired' ? 'Código expirado' : 'Pague com Pix'}
                        </h2>
                        <p>
                          {pixState === 'closed'
                            ? 'Este Pix não vale mais. Volte ao Reservei e toque em Assinar de novo.'
                            : pixState === 'qr-expired'
                              ? 'Gere um novo código para continuar. É a mesma cobrança, sem valor extra.'
                              : 'Abra o app do seu banco, escolha Pix e escaneie o QR Code ou cole o código.'}
                        </p>
                      </div>

                      {pixLoading && (
                        <p className="reservei-checkout__pix-pending" role="status">
                          <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Gerando o Pix…
                        </p>
                      )}

                      {!pixLoading && pixState === 'awaiting' && pix && (
                        <>
                          {pix.qr && (
                            <span className="reservei-checkout__pix-qr">
                              <img src={pix.qr} alt="QR Code do Pix" />
                            </span>
                          )}
                          <div className="reservei-checkout__pix-amount">
                            <span>Valor a pagar</span>
                            <strong>{formatPrice(pixAmount)}</strong>
                          </div>
                          {pix.payload && (
                            <PixCodeField payload={pix.payload} copyState={copyState} onCopy={() => void copyPixCode()} />
                          )}
                          <div className="reservei-checkout__pix-summary" aria-label="Resumo do Pix">
                            <span>
                              {plan.name} · {cycleLabel} · Pix
                            </span>
                            <strong>Primeira cobrança: {formatPrice(pixAmount)}</strong>
                          </div>
                          <p className="reservei-checkout__pix-instructions">
                            Assim que o banco confirmar, esta tela avança sozinha e seu plano é liberado no Reservei.
                          </p>
                        </>
                      )}

                      {!pixLoading && pixState === 'qr-unavailable' && (
                        <p className="reservei-checkout__alert" role="alert">
                          {/* Socorro na NOSSA página da fatura, não na do Asaas (20/10). */}
                          Não conseguimos carregar o QR Code agora. Tente carregar de novo
                          {watch ? (
                            <>
                              {' '}ou{' '}
                              <a className="reservei-checkout__inline-link" href={`/checkout/cobranca/${watch.chargeId}`}>
                                abra a página da fatura
                              </a>
                            </>
                          ) : null}
                          . A cobrança é a mesma, sem valor extra.
                        </p>
                      )}

                      {pollStalled && pixState !== 'closed' && (
                        <p className="reservei-checkout__alert reservei-checkout__alert--info" role="status">
                          Não conseguimos confirmar automaticamente. Se você já pagou, toque em Verificar pagamento.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}

                {stage === 'card' && (
                  <motion.div
                    key="card"
                    className="reservei-checkout__demo-stage reservei-checkout__demo-result reservei-checkout__demo-result--card"
                    initial={enter('forward')}
                    animate={{ opacity: 1, x: 0 }}
                    transition={enterTransition}
                    aria-labelledby="rc-card-title"
                    aria-live="polite"
                  >
                    {cardState === 'declined' || pollStalled ? (
                      <CircleAlert size={22} aria-hidden="true" />
                    ) : (
                      <Loader2 size={22} className="animate-spin" aria-hidden="true" />
                    )}
                    <div>
                      <strong id="rc-card-title" tabIndex={-1}>
                        {cardView.title}
                      </strong>
                      <span>{cardView.text}</span>
                    </div>
                  </motion.div>
                )}

                {stage === 'success' && (
                  <motion.div
                    key="success"
                    className="reservei-checkout__demo-stage reservei-checkout__success-stage"
                    initial={enter('forward')}
                    animate={{ opacity: 1, x: 0 }}
                    transition={enterTransition}
                    aria-labelledby="rc-success-title"
                  >
                    <span className="reservei-checkout__success-mark" aria-hidden="true">
                      <svg className="reservei-checkout__success-check" viewBox="0 0 64 64" focusable="false">
                        <circle className="reservei-checkout__success-ring" cx="32" cy="32" r="27" />
                        <path className="reservei-checkout__success-check-path" d="M19 33.5 28 42l17-20" />
                      </svg>
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--one" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--two" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--three" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--four" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--five" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--six" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--seven" />
                      <span className="reservei-checkout__success-ray reservei-checkout__success-ray--eight" />
                    </span>
                    <div className="reservei-checkout__success-copy">
                      <strong id="rc-success-title" tabIndex={-1}>
                        Tudo certo!
                      </strong>
                      <p className="reservei-checkout__success-qualification">
                        {wasTrial
                          ? `Seu teste do plano ${plan.name} começou.`
                          : `Pagamento confirmado. Seu plano ${plan.name} é liberado no Reservei em instantes.`}
                      </p>
                      <div className="reservei-checkout__success-summary" aria-label="Resumo da compra">
                        <span>
                          <b>Plano</b>
                          {plan.name}
                        </span>
                        <span>
                          <b>Ciclo</b>
                          {cycleLabel}
                        </span>
                        {!wasTrial && (
                          <span>
                            <b>Primeira cobrança</b>
                            {formatPrice(pix?.amount ?? firstCharge)}
                          </span>
                        )}
                        <span>
                          <b>Forma de pagamento</b>
                          {methodLabel}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}

                {inlineActions}

                <div className="reservei-checkout__support reservei-checkout__support--mobile">
                  <img src="/checkout/reservei/aceno.png" alt="" />
                  <p>
                    <span>Dúvidas na compra?</span>
                    <span>
                      <a className="reservei-checkout__inline-link" href={helpUrl} target="_blank" rel="noopener noreferrer">
                        Fale com o suporte do Reservei
                      </a>
                    </span>
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        )}
      </main>

      <Dialog.Root open={dockOpen} onOpenChange={setDockOpen}>
        <Dialog.Portal container={rootRef.current ?? undefined}>
          <Dialog.Overlay className="reservei-checkout__dock-overlay" />
          <Dialog.Content className="reservei-checkout__dock-dialog" aria-describedby="rc-dock-description">
            <div className="reservei-checkout__dock-dialog-header">
              <Dialog.Title>Resumo da compra</Dialog.Title>
              <Dialog.Description id="rc-dock-description">
                Plano, ciclo, forma de pagamento e valores.
              </Dialog.Description>
            </div>
            <div className="reservei-checkout__dock-dialog-body">
              <div className="reservei-checkout__dock-dialog-summary">
                {stage === 'plans' ? (
                  <>
                    <strong>Plano selecionado: {draftPlan?.name}</strong>
                    <span>O plano só muda quando você confirmar.</span>
                  </>
                ) : (
                  <>
                    <strong>
                      {plan.name} · {cycleLabel}
                    </strong>
                    <span>{formatPrice(firstCharge)} na primeira cobrança</span>
                    <span>{renewalText}</span>
                    <span>Forma de pagamento: {methodLabel}</span>
                    <span>{couponApplied ? `Cupom ${couponApplied.code} aplicado.` : 'Sem cupom.'}</span>
                    {plan.benefits.length > 0 && (
                      <ul>
                        {plan.benefits.map((benefit) => (
                          <li key={benefit}>{benefit}</li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="reservei-checkout__dock-dialog-actions">
              <button
                type="button"
                className="reservei-checkout__dock-cta"
                disabled={dockCta.disabled}
                onClick={(event) => {
                  if (event.detail > 1) return;
                  setDockOpen(false);
                  advance();
                }}
              >
                {dockCta.label}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <Dialog.Close asChild>
                <button type="button" className="reservei-checkout__dock-dialog-close">
                  Fechar resumo
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>

        {!isDesktop && (
          <div className={`reservei-checkout__action-dock${dockOpen ? ' is-dialog-open' : ''}`} ref={dockRef}>
            {actionTotal}
            <Dialog.Trigger asChild>
              <button type="button" className="reservei-checkout__dock-summary">
                Ver resumo
              </button>
            </Dialog.Trigger>
            {primaryAction}
          </div>
        )}
      </Dialog.Root>

      <Footer />
    </div>
  );
}
